import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import type {
  CommentVisibility,
  DashboardResponse,
  Paginated,
  Ticket,
  TicketAttachment,
  TicketComment,
  TicketEvent,
  TicketPriority,
  TicketStatus,
  TicketSummary,
} from '@electrodavisan/shared';
import { api, request } from './api';

export const ticketKeys = {
  dashboard: ['dashboard'] as const,
  mine: (filters: unknown) => ['tickets', 'mine', filters] as const,
  detail: (id: string) => ['tickets', id] as const,
  comments: (id: string) => ['tickets', id, 'comments'] as const,
  events: (id: string) => ['tickets', id, 'events'] as const,
  support: (filters: unknown) => ['support', 'tickets', filters] as const,
  supportCounts: ['support', 'inbox-counts'] as const,
};

/* ------------------------------------------------------------- dashboard */

export function useDashboard() {
  const auth = useAuth();

  return useQuery<DashboardResponse>({
    queryKey: ticketKeys.dashboard,
    queryFn: () => api.get<DashboardResponse>('/api/dashboard'),
    enabled: auth.isAuthenticated,
    staleTime: 30_000,
  });
}

/* --------------------------------------------------------- tickets propios */

export interface MyTicketFilters {
  page: number;
  pageSize: number;
  status?: TicketStatus[];
  search?: string;
}

export function useMyTickets(filters: MyTicketFilters) {
  const auth = useAuth();

  return useQuery<Paginated<TicketSummary>>({
    queryKey: ticketKeys.mine(filters),
    queryFn: () =>
      api.get<Paginated<TicketSummary>>('/api/tickets', {
        query: {
          page: filters.page,
          pageSize: filters.pageSize,
          status: filters.status,
          search: filters.search,
        },
      }),
    enabled: auth.isAuthenticated,
    placeholderData: (previous) => previous,
  });
}

/**
 * Detalle del ticket. `scope` decide contra que endpoint se pregunta:
 * el del usuario o el del panel de soporte. El backend aplica en ambos
 * casos sus propias comprobaciones de permisos.
 */
export function useTicket(id: string | undefined, scope: 'user' | 'support' = 'user') {
  const base = scope === 'support' ? '/api/support/tickets' : '/api/tickets';

  return useQuery<Ticket>({
    queryKey: [...ticketKeys.detail(id ?? ''), scope],
    queryFn: () => api.get<Ticket>(`${base}/${id}`),
    enabled: Boolean(id),
  });
}

export function useTicketComments(id: string | undefined, scope: 'user' | 'support' = 'user') {
  const base = scope === 'support' ? '/api/support/tickets' : '/api/tickets';

  return useQuery<TicketComment[]>({
    queryKey: [...ticketKeys.comments(id ?? ''), scope],
    queryFn: () => api.get<TicketComment[]>(`${base}/${id}/comments`),
    enabled: Boolean(id),
  });
}

export function useTicketEvents(id: string | undefined, scope: 'user' | 'support' = 'user') {
  const base = scope === 'support' ? '/api/support/tickets' : '/api/tickets';

  return useQuery<TicketEvent[]>({
    queryKey: [...ticketKeys.events(id ?? ''), scope],
    queryFn: () => api.get<TicketEvent[]>(`${base}/${id}/events`),
    enabled: Boolean(id),
  });
}

export interface CreateTicketPayload {
  scope: string;
  applicationId?: string | null;
  subject: string;
  category: string;
  description: string;
  actionPerformed?: string | null;
  stepsToReproduce?: string | null;
  errorMessage?: string | null;
  expectedResult?: string | null;
  actualResult?: string | null;
  priority: string;
  originUrl?: string | null;
}

export function useCreateTicket(onConfirmed?: () => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateTicketPayload) => {
      const ticket = await api.post<Ticket>('/api/tickets', payload);
      onConfirmed?.();
      return ticket;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.dashboard });
    },
  });
}

function invalidateTicket(queryClient: ReturnType<typeof useQueryClient>, ticketId: string): void {
  void queryClient.invalidateQueries({ queryKey: ['tickets', ticketId] });
  void queryClient.invalidateQueries({ queryKey: ['support'] });
  void queryClient.invalidateQueries({ queryKey: ticketKeys.dashboard });
  void queryClient.invalidateQueries({ queryKey: ['notifications'] });
}

export function useAddComment(scope: 'user' | 'support' = 'user') {
  const queryClient = useQueryClient();
  const base = scope === 'support' ? '/api/support/tickets' : '/api/tickets';

  return useMutation({
    mutationFn: ({
      ticketId,
      body,
      visibility,
    }: {
      ticketId: string;
      body: string;
      visibility: CommentVisibility;
    }) => api.post<TicketComment>(`${base}/${ticketId}/comments`, { body, visibility }),
    onSuccess: (_data, variables) => invalidateTicket(queryClient, variables.ticketId),
  });
}

export function useUploadAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, file }: { ticketId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      return request<TicketAttachment>(`/api/tickets/${ticketId}/attachments`, {
        method: 'POST',
        formData,
      });
    },
    onSuccess: (_data, variables) => invalidateTicket(queryClient, variables.ticketId),
  });
}

/**
 * Cierra el ticket propio.
 *
 * Vale desde cualquier estado menos «cerrado»: quien lo abrio puede darlo por
 * terminado aunque soporte no haya contestado todavia. Si se cierra de mas,
 * `useReopenTicket` lo devuelve a la vida.
 */
export function useCloseTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ticketId: string) => api.post<Ticket>(`/api/tickets/${ticketId}/close`),
    onSuccess: (_data, ticketId) => invalidateTicket(queryClient, ticketId),
  });
}

export function useReopenTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ticketId: string) => api.post<Ticket>(`/api/tickets/${ticketId}/reopen`),
    onSuccess: (_data, ticketId) => invalidateTicket(queryClient, ticketId),
  });
}

/* ------------------------------------------------------------- soporte */

export interface SupportTicketFilters {
  page: number;
  pageSize: number;
  inbox?: string;
  status?: TicketStatus[];
  priority?: TicketPriority[];
  applicationId?: string;
  assignedToSub?: string;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export function useSupportTickets(filters: SupportTicketFilters, enabled = true) {
  return useQuery<Paginated<TicketSummary>>({
    queryKey: ticketKeys.support(filters),
    queryFn: () =>
      api.get<Paginated<TicketSummary>>('/api/support/tickets', {
        query: {
          page: filters.page,
          pageSize: filters.pageSize,
          inbox: filters.inbox,
          status: filters.status,
          priority: filters.priority,
          applicationId: filters.applicationId,
          assignedToSub: filters.assignedToSub,
          search: filters.search,
          sortBy: filters.sortBy,
          sortDir: filters.sortDir,
        },
      }),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useSupportInboxCounts(enabled = true) {
  return useQuery<Record<string, number>>({
    queryKey: ticketKeys.supportCounts,
    queryFn: () => api.get<Record<string, number>>('/api/support/tickets/inbox-counts'),
    enabled,
    staleTime: 30_000,
  });
}

export function useAssignTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ticketId,
      assigneeSub,
    }: {
      ticketId: string;
      // `undefined` = tomarlo yo; `null` = liberarlo.
      assigneeSub?: string | null;
    }) =>
      api.post<Ticket>(
        `/api/support/tickets/${ticketId}/assign`,
        assigneeSub === undefined ? {} : { assigneeSub },
      ),
    onSuccess: (_data, variables) => invalidateTicket(queryClient, variables.ticketId),
  });
}

export function useChangeStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ticketId,
      status,
      message,
    }: {
      ticketId: string;
      status: TicketStatus;
      message?: string;
    }) => api.post<Ticket>(`/api/support/tickets/${ticketId}/status`, { status, message }),
    onSuccess: (_data, variables) => invalidateTicket(queryClient, variables.ticketId),
  });
}

export function useChangePriority() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ticketId,
      priority,
      message,
    }: {
      ticketId: string;
      priority: TicketPriority;
      message?: string;
    }) => api.post<Ticket>(`/api/support/tickets/${ticketId}/priority`, { priority, message }),
    onSuccess: (_data, variables) => invalidateTicket(queryClient, variables.ticketId),
  });
}
